<script lang="ts">
/** One filterable column offered in the "Add filter" menu. */
export interface FilterConfig {
  key: string;
  label: string;
}
</script>

<script setup lang="ts">
import { computed, nextTick, ref, watch, type PropType } from 'vue';
import GaiaAddButton from '../base/GaiaAddButton.vue';
import GaiaPopover from '../base/GaiaPopover.vue';
import GaiaCard from '../base/GaiaCard.vue';
import GaiaSearchInput from '../base/inputs/GaiaSearchInput.vue';
import GaiaPickerOption from './GaiaPickerOption.vue';
import GaiaText from '../base/GaiaText.vue';
import GaiaFilterBar from './GaiaFilterBar.vue';
import GaiaFilterChip from './GaiaFilterChip.vue';
import GaiaFilterValuePanel, { type GaiaFilterValue } from './GaiaFilterValuePanel.vue';

/** Backend `null` has no place in a query string, so it travels as this. */
const EMPTY_SENTINEL = '(Empty)';
/** Pills shown on a chip before the rest collapse into "+N". */
const MAX_PILLS = 3;

const props = defineProps({
  /** The columns that may be filtered — usually every column the list shows. */
  filterConfig: {
    type: Array as PropType<FilterConfig[]>,
    required: true,
  },
  /**
   * Column key → the values present in the data, straight from the search
   * endpoint's `filter_options`. A column with no options is not offered.
   */
  filterOptions: {
    type: Object as PropType<Record<string, (string | null)[] | undefined>>,
    required: true,
  },
  /** Column key → the values currently filtered on. The source of truth. */
  activeFilters: {
    type: Object as PropType<Record<string, unknown>>,
    required: true,
  },
  /**
   * Column key → value → display label, for values that arrive as opaque ids
   * (a user email, a kanban state uid). Unmapped values show as themselves.
   */
  valueLabelMap: {
    type: Object as PropType<Record<string, Record<string, string>> | undefined>,
    default: undefined,
  },
  /** v-model:visible — the caller owns the Filter toggle in its own toolbar. */
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  'update:filters': [filters: Record<string, unknown>];
  'update:visible': [visible: boolean];
}>();

const addMenuOpen = ref(false);
const openChipKey = ref<string | null>(null);
/**
 * A column that has a chip but no chosen values yet — the state between picking
 * "Assigned user" from the add menu and picking a user. Chips are otherwise
 * derived from `activeFilters`, which by definition has no entry for a column
 * nobody has filtered on, so without this the new chip has nothing to render
 * and its value panel has nothing to anchor to.
 */
const pendingKey = ref<string | null>(null);
const propertySearch = ref('');
const valueSearch = ref('');

/**
 * Filters as this component thinks of them: one column, many values. A Map
 * rather than a Record so a missing column reads as `undefined` instead of
 * TypeScript pretending every key is present.
 */
const selection = computed<Map<string, string[]>>(() => {
  const out = new Map<string, string[]>();
  for (const [key, value] of Object.entries(props.activeFilters)) {
    if (Array.isArray(value) && value.length > 0) {
      out.set(key, value.map(String));
    }
  }
  return out;
});

const hasActiveFilters = computed(() => selection.value.size > 0);

function labelFor(property: string, value: string): string {
  // Booleans read as Yes/No: the people using these lists are lawyers, not
  // engineers, and a column of "true" is nobody's idea of an answer.
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  const forProperty: Record<string, string> | undefined = props.valueLabelMap?.[property];
  return forProperty?.[value] ?? value;
}

/** One chip per filtered column, plus the one still being chosen. */
const chips = computed(() => {
  const keys = [...selection.value.keys()];
  if (pendingKey.value && !selection.value.has(pendingKey.value)) keys.push(pendingKey.value);

  return keys.map(key => {
    const config = props.filterConfig.find(item => item.key === key);
    const labels = (selection.value.get(key) ?? []).map(value => labelFor(key, value));
    return {
      key,
      field: config?.label ?? key,
      // No pills yet reads as "Any" on the chip, which is what an unfilled
      // filter means.
      pills: labels.slice(0, MAX_PILLS),
      overflow: labels.length > MAX_PILLS ? `+${labels.length - MAX_PILLS}` : undefined,
    };
  });
});

/** Columns that have something to filter by and are not already on a chip. */
const addableProperties = computed(() => {
  const query = propertySearch.value.trim().toLowerCase();
  return props.filterConfig
    .filter(config => (props.filterOptions[config.key]?.length ?? 0) > 0)
    .filter(config => !selection.value.has(config.key) && config.key !== pendingKey.value)
    .filter(config => !query || config.label.toLowerCase().includes(query));
});

/** The open chip's values, checked state and all, filtered by the panel search. */
const openChipValues = computed<GaiaFilterValue[]>(() => {
  const key = openChipKey.value;
  if (!key) return [];

  const picked = new Set(selection.value.get(key) ?? []);
  const query = valueSearch.value.trim().toLowerCase();

  return (props.filterOptions[key] ?? [])
    .map(option => {
      const value = option ?? EMPTY_SENTINEL;
      return { key: value, label: labelFor(key, value), checked: picked.has(value) };
    })
    .filter(item => !query || item.label.toLowerCase().includes(query));
});

const openChipPickedLabel = computed(() => {
  const key = openChipKey.value;
  const count = key ? (selection.value.get(key)?.length ?? 0) : 0;
  return count > 0 ? `${count} selected` : undefined;
});

function commit(next: Map<string, string[]>): void {
  // Drop emptied columns rather than emitting `key: []`, which reads downstream
  // as "filter on nothing" instead of "do not filter".
  const cleaned: Record<string, unknown> = {};
  for (const [key, values] of next) {
    if (values.length > 0) cleaned[key] = values;
  }
  emit('update:filters', cleaned);
}

function addProperty(key: string): void {
  addMenuOpen.value = false;
  propertySearch.value = '';
  // The chip appears with no values yet and opens straight onto its value
  // panel, so adding a filter is one gesture rather than two.
  pendingKey.value = key;
  // Open on the next tick so the chip is in the DOM first — a popover told to
  // show while its trigger is still mounting has nothing to anchor to.
  void nextTick(() => {
    openChipKey.value = key;
  });
}

/**
 * Closing a chip that never got a value drops it again — an empty chip filters
 * nothing, so leaving it on the bar would just be litter.
 */
function onChipPopoverToggle(key: string, show: boolean): void {
  openChipKey.value = show ? key : null;
  if (!show && pendingKey.value === key && !selection.value.has(key)) {
    pendingKey.value = null;
  }
}

function toggleValue(value: string): void {
  const key = openChipKey.value;
  if (!key) return;

  const current = selection.value.get(key) ?? [];
  const next = new Map(selection.value);
  next.set(
    key,
    current.includes(value) ? current.filter(item => item !== value) : [...current, value]
  );
  commit(next);
}

function removeChip(key: string): void {
  const next = new Map(selection.value);
  next.delete(key);
  if (openChipKey.value === key) openChipKey.value = null;
  if (pendingKey.value === key) pendingKey.value = null;
  commit(next);
}

function removePill(key: string, label: string): void {
  const current = selection.value.get(key) ?? [];
  const next = new Map(selection.value);
  next.set(
    key,
    current.filter(value => labelFor(key, value) !== label)
  );
  commit(next);
}

function clearOpenChip(): void {
  if (openChipKey.value) removeChip(openChipKey.value);
}

function reset(): void {
  openChipKey.value = null;
  pendingKey.value = null;
  emit('update:filters', {});
}

// Filters restored from the URL on mount should show without the user pressing
// Filter again, so the bar opens itself whenever something is active.
watch(
  hasActiveFilters,
  active => {
    if (active && !props.visible) emit('update:visible', true);
  },
  { immediate: true }
);

// A hidden bar leaves no menus open behind it.
watch(
  () => props.visible,
  visible => {
    if (!visible) {
      addMenuOpen.value = false;
      openChipKey.value = null;
      pendingKey.value = null;
      propertySearch.value = '';
      valueSearch.value = '';
    }
  }
);

watch(openChipKey, () => {
  valueSearch.value = '';
});
</script>

<template>
  <!-- The filter row under a list's toolbar: one chip per filtered column, each
       opening a searchable value panel, plus an "Add filter" menu of the columns
       that have something to filter by.

       One chip per *column*, not per value — `activeFilters` is already
       column → values, and a column with four picked values reads better as one
       chip with four pills than as four chips repeating the column name. -->
  <gaia-filter-bar v-if="props.visible" :show-reset="hasActiveFilters" @reset="reset">
    <gaia-popover
      v-for="chip in chips"
      :key="chip.key"
      :show="openChipKey === chip.key"
      @update:show="(show: boolean) => onChipPopoverToggle(chip.key, show)"
    >
      <template #trigger>
        <gaia-filter-chip
          :field="chip.field"
          :values="chip.pills"
          :overflow="chip.overflow"
          :active="openChipKey === chip.key"
          @toggle="openChipKey = openChipKey === chip.key ? null : chip.key"
          @remove="removeChip(chip.key)"
          @remove-value="(label: string) => removePill(chip.key, label)"
        />
      </template>
      <!-- The panel brings its own surface (GaiaPopover is `raw`). -->
      <gaia-filter-value-panel
        :values="openChipValues"
        :search="valueSearch"
        :picked-label="openChipPickedLabel"
        @update:search="(value: string) => (valueSearch = value)"
        @toggle="toggleValue"
        @clear="clearOpenChip"
      />
    </gaia-popover>

    <template #add>
      <gaia-popover v-model:show="addMenuOpen">
        <template #trigger>
          <gaia-add-button subject="filter" variant="dashed" />
        </template>
        <gaia-card content-style="padding: 6px" :style="{ width: '232px' }">
          <gaia-search-input
            v-model:value="propertySearch"
            variant="panel"
            placeholder="Search columns"
            :style="{ marginBottom: '6px' }"
          />
          <gaia-picker-option
            v-for="config in addableProperties"
            :key="config.key"
            :label="config.label"
            @select="addProperty(config.key)"
          />
          <gaia-text
            v-if="addableProperties.length === 0"
            tag="div"
            size="meta"
            tone="subtle"
            :style="{ padding: '8px 9px' }"
          >
            Every column is already filtered.
          </gaia-text>
        </gaia-card>
      </gaia-popover>
    </template>
  </gaia-filter-bar>
</template>
